// client/src/pages/secure/CrmContactPage.js — one person or company: who they are,
// whether we may contact them, what happened, what is next, and the business with them.
//
// The Call / Text / Email buttons ask Contact Compliance first (POST .../check) and only
// then show a tel:/sms:/mailto: link. The CRM never sends anything itself; the user's
// phone or mail program does, and the touch is logged here afterwards.
//
// Under contract and closed are recorded on a transaction. "Create the transaction"
// uses the existing transaction endpoint, then links it, so the transaction flow
// (deadlines, file review, automation) runs exactly as it does for any other deal.

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Pill, Table, Modal, Input, Select, Textarea, Button, Notice, Field, Loading,
    money, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';
import { ContactForm, STAGE_TONE, DNC_PILL, TouchPill, nameOf } from './CrmPage';
import HotLeadPanel from '../../components/secure/HotLeadPanel';

const nowLocal = () => {
    const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
};

// ---------------------------------------------------------------- may I contact them?
const ContactGate = ({ contact, onLog }) => {
    const api = useApiClient();
    const [purpose, setPurpose] = useState(contact.client_id ? 'service' : 'prospecting');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const check = async (channel) => {
        setBusy(true); setError(''); setResult(null);
        try { setResult({ channel, ...(await api.post(`/api/crm/contacts/${contact.contact_id}/check`, { channel, purpose })) }); }
        catch (e) { setError(e.message); } finally { setBusy(false); }
    };
    const verb = { call: 'Call', text: 'Text', email: 'Email' };
    return (
        <div>
            <div className="flex flex-wrap gap-2 items-end">
                <Select label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} className="w-56"
                    options={[{ value: 'prospecting', label: 'Prospecting / marketing' }, { value: 'transactional', label: 'About an active deal' },
                        { value: 'service', label: 'Service to an existing client' }]} />
                <Button className="mb-3" disabled={busy || !contact.phone} onClick={() => check('call')}>Call</Button>
                <Button className="mb-3" disabled={busy || !contact.phone} onClick={() => check('text')}>Text</Button>
                <Button className="mb-3" disabled={busy || !contact.email} onClick={() => check('email')}>Email</Button>
            </div>
            {error && <Notice message={error} />}
            {result && (
                <Notice tone={result.allowed ? 'good' : 'bad'}
                    title={result.allowed ? `${verb[result.channel]} allowed — the check is on record` : `${verb[result.channel]} refused`}
                    problems={result.reasons.map((r) => `${r.blocks ? 'BLOCKS: ' : ''}${r.message}${r.authority ? ` (${r.authority})` : ''}`)} />
            )}
            {result?.allowed && (
                <div className="flex flex-wrap gap-2 items-center mb-2">
                    <a href={result.href} className="px-3 py-2 rounded text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700">
                        {result.channel === 'call' ? `Dial ${contact.phone}` : result.channel === 'text' ? `Text ${contact.phone}` : `Write to ${contact.email}`}</a>
                    <Button variant="ghost" onClick={() => onLog({ activity_type: { call: 'Call', text: 'Text', email: 'Email' }[result.channel], direction: 'Outbound', purpose })}>
                        Log it afterwards</Button>
                    {result.reminder && <span className="text-xs text-slate-500">{result.reminder}</span>}
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------- log a touch
const LogModal = ({ meta, contact, preset, onClose, onSaved }) => {
    const api = useApiClient();
    const [f, setF] = useState({ activity_type: 'Call', direction: 'Outbound', purpose: '', subject: '', body: '',
        occurred_at: nowLocal(), opportunity_id: '', follow_up_on: '', follow_up_title: '', ...preset });
    const [error, setError] = useState('');
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const save = async () => {
        try {
            const r = await api.post(`/api/crm/contacts/${contact.contact_id}/activities`,
                { ...f, occurred_at: new Date(f.occurred_at).toISOString() });
            onSaved(r);
        } catch (e) { setError(e.message); }
    };
    const open = (contact.opportunities || []).filter((o) => meta.open_stages.includes(o.stage));
    return (
        <Modal title={`Log a touch with ${nameOf(contact)}`} onClose={onClose}>
            {error && <Notice message={error} />}
            <div className="grid grid-cols-2 gap-x-3">
                <Select label="What" value={f.activity_type} onChange={set('activity_type')} options={meta.activity_types.map((t) => ({ value: t, label: t }))} />
                <Select label="Direction" value={f.direction} onChange={set('direction')} options={['Outbound', 'Inbound', 'N/A'].map((t) => ({ value: t, label: t }))} />
                <Select label="Purpose" value={f.purpose} onChange={set('purpose')}
                    options={[{ value: '', label: '—' }, ...meta.purposes.map((p) => ({ value: p, label: titleCase(p) }))]} />
                <Input label="When" type="datetime-local" value={f.occurred_at} onChange={set('occurred_at')} />
                {open.length > 0 && (
                    <Select label="About" value={f.opportunity_id} onChange={set('opportunity_id')} className="col-span-2"
                        options={[{ value: '', label: 'General' }, ...open.map((o) => ({ value: o.opportunity_id, label: o.title || titleCase(o.opp_type) }))]} />
                )}
            </div>
            <Input label="Subject" value={f.subject} onChange={set('subject')} />
            <Textarea label="Notes" value={f.body} onChange={set('body')} />
            <div className="grid grid-cols-2 gap-x-3">
                <Input label="Follow up on" type="date" value={f.follow_up_on} onChange={set('follow_up_on')}
                    hint={meta.settings?.auto_follow_up_task ? `Blank = automatic, ${meta.settings.follow_up_days} days out` : 'Optional — creates a task'} />
                <Input label="Follow-up title" value={f.follow_up_title} onChange={set('follow_up_title')} placeholder={`Follow up with ${nameOf(contact)}`} />
            </div>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save}>Log</Button></div>
        </Modal>
    );
};

// ---------------------------------------------------------------- opportunities
const OppModal = ({ meta, contact, opp, agents, onClose, onSaved }) => {
    const api = useApiClient();
    const [f, setF] = useState({
        opp_type: 'buyer', commercial_side: '', title: '', estimated_value: '', commission_pct: '', expected_close_on: '',
        source: contact.source || '', area: contact.area || contact.city || '', assigned_agent_id: '',
        ...(opp ? { ...opp, expected_close_on: opp.expected_close_on ? String(opp.expected_close_on).slice(0, 10) : '' } : {}),
    });
    const [error, setError] = useState('');
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
    const save = async () => {
        const body = { ...f };
        if (!meta.can_reassign || !opp) delete body.assigned_agent_id;
        if (!opp && meta.sees_everyone && f.assigned_agent_id) body.assigned_agent_id = f.assigned_agent_id;
        try {
            if (opp) await api.put(`/api/crm/opportunities/${opp.opportunity_id}`, body);
            else await api.post('/api/crm/opportunities', { ...body, contact_id: contact.contact_id });
            onSaved();
        } catch (e) { setError(e.message); }
    };
    const signed = !!opp?.client_id;
    return (
        <Modal title={opp ? 'Edit opportunity' : 'New opportunity'} onClose={onClose}>
            {error && <Notice message={error} />}
            <div className="grid grid-cols-2 gap-x-3">
                <Select label="Type" value={f.opp_type} onChange={set('opp_type')} disabled={signed} options={meta.opp_types} />
                {f.opp_type === 'commercial'
                    ? <Select label="Our side" value={f.commercial_side || ''} onChange={set('commercial_side')} disabled={signed}
                        options={[{ value: '', label: '—' }, ...meta.sides.map((s) => ({ value: s, label: titleCase(s) }))]} />
                    : <div />}
                <Input label="Title" value={f.title || ''} onChange={set('title')} placeholder="e.g. 3-bed in Oak Park, under $600k" className="col-span-2" />
                <Input label="Estimated price / value" type="number" min="0" value={f.estimated_value ?? ''} onChange={set('estimated_value')} />
                <Input label="Commission %" type="number" min="0" max="100" step="0.01" value={f.commission_pct ?? ''} onChange={set('commission_pct')} />
                <Input label="Expected close" type="date" value={f.expected_close_on || ''} onChange={set('expected_close_on')} />
                <Input label="Area" value={f.area || ''} onChange={set('area')} />
                <Input label="Source" value={f.source || ''} onChange={set('source')} />
                {((!opp && meta.sees_everyone) || (opp && meta.can_reassign)) && agents.length > 0 && (
                    <Select label="Agent" value={f.assigned_agent_id ?? ''} onChange={set('assigned_agent_id')}
                        options={[{ value: '', label: '— card\'s agent —' }, ...agents.map((a) => ({ value: a.agent_id, label: a.name }))]} />
                )}
            </div>
            {signed && <p className="text-xs text-slate-500 mb-3">The type is fixed once signed: the client record carries it.</p>}
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save}>Save</Button></div>
        </Modal>
    );
};

const StageModal = ({ meta, opp, to, onClose, onSaved }) => {
    const api = useApiClient();
    const [f, setF] = useState({ lost_reason: '', lost_note: '', note: '' });
    const [error, setError] = useState(null);
    const label = meta.stages.find((s) => s.key === to)?.label;
    const save = async () => {
        try { const r = await api.post(`/api/crm/opportunities/${opp.opportunity_id}/stage`, { to_stage: to, ...f }); onSaved(r.message); }
        catch (e) { setError({ message: e.message, problems: e.problems }); }
    };
    return (
        <Modal title={`Move to ${label}`} onClose={onClose}>
            {error && <Notice message={error.message} problems={(error.problems || []).slice(1)} />}
            {to === 'agreement_signed' && !opp.client_id && (
                <Notice tone="info" message="Signing makes this person a client. If they are not one already, a client record is created from the card (or linked, if one with this email exists in your book), so the forms engine and client checklist run on it." />
            )}
            {to === 'lost' && (
                <>
                    <Select label="Why" value={f.lost_reason} onChange={(e) => setF((x) => ({ ...x, lost_reason: e.target.value }))}
                        options={[{ value: '', label: '— choose —' }, ...meta.lost_reasons.map((r) => ({ value: r.key, label: r.label }))]} />
                    <Input label="Note" value={f.lost_note} onChange={(e) => setF((x) => ({ ...x, lost_note: e.target.value }))} />
                </>
            )}
            {to !== 'lost' && <Input label="Note (optional)" value={f.note} onChange={(e) => setF((x) => ({ ...x, note: e.target.value }))} />}
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save}>Move</Button></div>
        </Modal>
    );
};

/**
 * Hand the deal to the transaction flow: link a transaction the client is already on,
 * or create one through the existing /api/transactions endpoint and link it.
 */
const TransactionModal = ({ contact, opp, onClose, onSaved }) => {
    const api = useApiClient();
    const [existing, setExisting] = useState(null);
    const [properties, setProperties] = useState([]);
    const [f, setF] = useState({ transaction_id: '', property_id: '', status: 'Pending', listing_date: '' });
    const [error, setError] = useState('');
    const clientId = opp.client_id || contact.client_id;
    useEffect(() => {
        if (clientId) api.get(`/api/clients/${clientId}/transactions`).then(setExisting).catch(() => setExisting([]));
        else setExisting([]);
        api.get('/api/properties').then(setProperties).catch(() => setProperties([]));
    }, [api, clientId]);
    const side = opp.opp_type === 'commercial' ? opp.commercial_side : opp.opp_type;
    const role = { buyer: 'Buyer', seller: 'Seller', landlord: 'Landlord', tenant: 'Tenant' }[side];
    const link = async (tid) => {
        const r = await api.post(`/api/crm/opportunities/${opp.opportunity_id}/link-transaction`, { transaction_id: tid });
        onSaved((r.warnings || []).join(' ') || `Linked to transaction #${tid}.`);
    };
    const create = async () => {
        setError('');
        if (!clientId) { setError('Move the opportunity to "Agreement signed" first — that creates the client the transaction needs.'); return; }
        try {
            const t = await api.post('/api/transactions', {
                property_id: f.property_id,
                transaction_type: side === 'landlord' || side === 'tenant' ? 'Lease' : 'Sale',
                status: f.status, listing_date: f.listing_date || null,
                participants: { clients: [{ client_id: clientId, client_role: role }],
                                agents: opp.assigned_agent_id ? [{ agent_id: opp.assigned_agent_id }] : [] },
            });
            await link(t.transaction_id);
        } catch (e) { setError(e.message); }
    };
    return (
        <Modal title="Hand off to a transaction" subtitle="From here the transaction is the record: deadlines, documents, file review." onClose={onClose}>
            {error && <Notice message={error} />}
            <h3 className="text-sm font-semibold mb-2">Link an existing transaction</h3>
            {!existing ? <Loading /> : existing.length === 0 ? <p className="text-sm text-slate-500 mb-4">This client is not on any transaction yet.</p> : (
                <div className="flex gap-2 items-end mb-4">
                    <Select label="Transaction" value={f.transaction_id} onChange={(e) => setF((x) => ({ ...x, transaction_id: e.target.value }))} className="flex-1"
                        options={[{ value: '', label: '— choose —' }, ...existing.map((t) => ({ value: t.transaction_id,
                            label: `#${t.transaction_id} ${t.street_address || ''} — ${t.status} (${t.client_role})` }))]} />
                    <Button className="mb-3" disabled={!f.transaction_id} onClick={() => link(Number(f.transaction_id)).catch((e) => setError(e.message))}>Link</Button>
                </div>
            )}
            <h3 className="text-sm font-semibold mb-2 border-t pt-3">Or create the transaction</h3>
            <div className="grid grid-cols-2 gap-x-3">
                <Select label="Property" value={f.property_id} onChange={(e) => setF((x) => ({ ...x, property_id: e.target.value }))} className="col-span-2"
                    options={[{ value: '', label: '— choose —' }, ...properties.map((p) => ({ value: p.property_id, label: `${p.street_address}, ${p.city || ''}` }))]} />
                <Select label="Status" value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value }))}
                    options={['Active Listing', 'Pending'].map((s) => ({ value: s, label: s }))} />
                <Input label="Listing date" type="date" value={f.listing_date} onChange={(e) => setF((x) => ({ ...x, listing_date: e.target.value }))} />
            </div>
            <p className="text-xs text-slate-500 mb-3">Property not listed? Add it on <Link className="text-indigo-700 hover:underline" to="/dashboard/properties">Properties</Link> first. The client is added as {role || 'a party'}; add the other side and the contract acceptance date on the transaction page.</p>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button disabled={!f.property_id} onClick={create}>Create and link</Button></div>
        </Modal>
    );
};

// ---------------------------------------------------------------- page
const CrmContactPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = useApiClient();
    const [meta, setMeta] = useState(null);
    const [c, setC] = useState(null);
    const [agents, setAgents] = useState([]);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState(null);
    const [modal, setModal] = useState(null);
    const [task, setTask] = useState({ title: '', due_date: '' });

    const load = useCallback(() => {
        api.get(`/api/crm/contacts/${id}`).then(setC).catch((e) => setError(e.message));
    }, [api, id]);
    useEffect(() => {
        api.get('/api/crm/meta').then((m) => {
            setMeta(m);
            if (m.sees_everyone) api.get('/api/agents').then((a) => setAgents(a || [])).catch(() => {});
        }).catch((e) => setError(e.message));
        load();
    }, [api, load]);

    const act = async (fn, okMsg) => {
        try { const r = await fn(); setNotice({ tone: 'good', message: (r && r.message) || okMsg }); setModal(null); load(); }
        catch (e) { setNotice({ tone: 'bad', message: e.message, problems: e.problems }); }
    };
    const saveCard = (body) => act(() => api.put(`/api/crm/contacts/${c.contact_id}`, body), 'Saved.');
    const archive = () => {
        if (!window.confirm('Archive this card? It is kept on file and can be restored.')) return;
        act(() => api.delete(`/api/crm/contacts/${c.contact_id}`));
    };
    const addTask = (e) => {
        e.preventDefault();
        act(() => api.post(`/api/crm/contacts/${c.contact_id}/tasks`, task).then(() => setTask({ title: '', due_date: '' })), 'Follow-up scheduled.');
    };
    const taskDone = (t, status) => act(() => api.put(`/api/crm/tasks/${t.task_id}`, { status }), 'Updated.');

    if (error) return <div className="p-8"><Notice message={error} /><Link to="/dashboard/crm?tab=contacts" className="text-indigo-700">Back to contacts</Link></div>;
    if (!c || !meta) return <Loading />;
    const [dncLabel, dncTone] = DNC_PILL[c.compliance?.summary] || DNC_PILL.unknown;
    const open = c.tasks.filter((t) => t.status === 'Open');
    return (
        <div className="p-4 md:p-8 space-y-6">
            <PageHeader title={nameOf(c)}
                subtitle={[c.job_title, c.company_name && (c.first_name || c.last_name) ? c.company_name : null, titleCase(c.category), c.agent_name ? `Agent: ${c.agent_name}` : 'Office pool'].filter(Boolean).join(' · ')}>
                <Button variant="ghost" onClick={() => navigate('/dashboard/crm?tab=contacts')}>All contacts</Button>
                {c.deleted_at
                    ? <Button onClick={() => act(() => api.post(`/api/crm/contacts/${c.contact_id}/restore`, {}), 'Restored.')}>Restore</Button>
                    : <>
                        <Button variant="ghost" onClick={() => setModal({ kind: 'edit' })}>Edit</Button>
                        <Button variant="ghost" onClick={archive}>Archive</Button>
                        <Button onClick={() => setModal({ kind: 'log', preset: {} })}>Log a touch</Button>
                    </>}
            </PageHeader>
            {notice && <Notice tone={notice.tone} message={notice.message} problems={notice.problems} onDismiss={() => setNotice(null)} />}
            {c.deleted_at && <Notice tone="warn" message={`Archived ${fmtDate(c.deleted_at)}.`} />}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Card" className="xl:col-span-1">
                    <dl>
                        <Field label="Email" value={c.email} />
                        <Field label="Phone" value={c.phone && <>{c.phone}{c.alt_phone ? ` · ${c.alt_phone}` : ''}</>} />
                        <Field label="Address" value={[c.street_address, c.city, c.state, c.zip_code].filter(Boolean).join(', ')} />
                        <Field label="Area" value={c.area} />
                        <Field label="Source" value={c.source} />
                        <Field label="Tags" value={c.tags && <span className="flex flex-wrap gap-1">{c.tags.split(',').map((t) => <Pill key={t} tone="indigo">{t}</Pill>)}</span>} />
                        <Field label="Prefers" value={titleCase(c.preferred_channel)} />
                        <Field label="Last touch" value={c.last_contacted_at ? fmtDate(c.last_contacted_at) : 'never'} />
                        <Field label="Keep in touch" value={<TouchPill touch={c.touch} />}
                            hint={c.touch.cadence_days ? `Every ${c.touch.cadence_days} days` : undefined} />
                        <Field label="Client record" value={c.client_id
                            ? <Link className="text-indigo-700 hover:underline" to={`/dashboard/clients/${c.client_id}`}>{c.client_type} · {c.client_pipeline_stage}</Link>
                            : null} hint={c.client_id ? 'Name, email, phone and address live on the client record.' : 'Becomes a client when an agreement is signed.'} />
                        {c.notes && <Field label="Notes" value={<span className="whitespace-pre-wrap">{c.notes}</span>} />}
                    </dl>
                </Panel>
                <div className="xl:col-span-2 space-y-6">
                    <Panel title="May we contact them?" actions={<Pill tone={dncTone}>{dncLabel}</Pill>}
                        subtitle="Each button runs the do-not-call check and records it. The link appears only if the answer is yes.">
                        {c.compliance?.suppressed && (c.compliance.suppressed.call || c.compliance.suppressed.text || c.compliance.suppressed.email) && (
                            <Notice tone="warn" message={`Opted out of: ${['call', 'text', 'email'].filter((k) => c.compliance.suppressed[k]).join(', ')}. Opt-outs are recorded in Contact Compliance and are permanent.`} />
                        )}
                        {!c.deleted_at && <ContactGate contact={c} onLog={(preset) => setModal({ kind: 'log', preset })} />}
                    </Panel>
                    <HotLeadPanel contactId={c.contact_id} />
                    <Panel title="Opportunities" actions={!c.deleted_at && <Button variant="subtle" onClick={() => setModal({ kind: 'opp' })}>New opportunity</Button>}>
                        {c.opportunities.length === 0 ? <p className="text-sm text-slate-500">No business with this contact yet.</p> : (
                            <div className="space-y-3">
                                {c.opportunities.map((o) => {
                                    const idx = meta.stages.findIndex((s) => s.key === o.stage);
                                    const next = meta.stages[idx + 1];
                                    const final = ['closed'].includes(o.stage);
                                    return (
                                        <div key={o.opportunity_id} className="border border-slate-200 rounded-lg p-3">
                                            <div className="flex flex-wrap justify-between gap-2">
                                                <div>
                                                    <div className="font-medium text-slate-800">{o.title || `${titleCase(o.opp_type)} opportunity`}</div>
                                                    <div className="text-xs text-slate-500">{titleCase(o.opp_type)}{o.commercial_side ? ` (${o.commercial_side})` : ''}
                                                        {' · '}{money(o.estimated_value)}{o.gci ? ` · est. GCI ${money(o.gci)}` : ''}
                                                        {o.expected_close_on ? ` · close ${fmtDate(o.expected_close_on)}` : ''}{o.agent_name ? ` · ${o.agent_name}` : ''}</div>
                                                    {o.stage === 'lost' && <div className="text-xs text-rose-700">Lost: {meta.lost_reasons.find((r) => r.key === o.lost_reason)?.label}{o.lost_note ? ` — ${o.lost_note}` : ''}</div>}
                                                    {o.transaction_id && <div className="text-xs mt-1">Transaction <Link className="text-indigo-700 hover:underline" to={`/dashboard/transactions/${o.transaction_id}`}>#{o.transaction_id}</Link> — {o.transaction_status}</div>}
                                                </div>
                                                <Pill tone={STAGE_TONE[o.stage]}>{meta.stages[idx]?.label || o.stage}</Pill>
                                            </div>
                                            {o.transaction_suggests && (
                                                <Notice tone="warn" message={`The transaction is ${o.transaction_status}; this opportunity is still ${meta.stages[idx]?.label}.`} />
                                            )}
                                            {!final && !c.deleted_at && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {o.transaction_suggests && (
                                                        <Button onClick={() => setModal({ kind: 'stage', opp: o, to: o.transaction_suggests.to })}>
                                                            Match the transaction ({titleCase(o.transaction_suggests.to)})</Button>
                                                    )}
                                                    {next && next.key !== 'lost' && !['under_contract', 'closed'].includes(next.key) && (
                                                        <Button variant="subtle" onClick={() => setModal({ kind: 'stage', opp: o, to: next.key })}>→ {next.label}</Button>
                                                    )}
                                                    {o.stage === 'lost' && <Button variant="subtle" onClick={() => setModal({ kind: 'stage', opp: o, to: 'first_contact' })}>Reopen</Button>}
                                                    {!o.transaction_id && ['agreement_signed', 'under_contract'].includes(o.stage) && (
                                                        <Button variant="subtle" onClick={() => setModal({ kind: 'txn', opp: o })}>Hand off to a transaction</Button>
                                                    )}
                                                    {!o.transaction_id && o.stage !== 'lost' && o.stage !== 'agreement_signed' && (
                                                        <span className="text-xs text-slate-400 self-center">Under contract and closed come from the linked transaction.</span>
                                                    )}
                                                    {o.stage !== 'lost' && <Button variant="ghost" onClick={() => setModal({ kind: 'stage', opp: o, to: 'lost' })}>Lost</Button>}
                                                    <Button variant="ghost" onClick={() => setModal({ kind: 'opp', opp: o })}>Edit</Button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Panel>
                    <Panel title="Follow-ups" subtitle="These are ordinary tasks: they also appear on the Tasks page.">
                        {!c.deleted_at && (
                            <form onSubmit={addTask} className="flex flex-wrap gap-2 items-end">
                                <Input label="Follow-up" value={task.title} onChange={(e) => setTask((x) => ({ ...x, title: e.target.value }))} className="flex-1 min-w-[12rem]" />
                                <Input label="Due" type="date" value={task.due_date} onChange={(e) => setTask((x) => ({ ...x, due_date: e.target.value }))} />
                                <Button className="mb-3" disabled={!task.title.trim()}>Add</Button>
                            </form>
                        )}
                        <Table columns={[
                            { key: 'due_date', label: 'Due', nowrap: true, render: (r) => fmtDate(r.due_date) },
                            { key: 'title', label: 'Follow-up' },
                            { key: 'assigned_to_name', label: 'Agent' },
                            { key: 'status', label: 'Status', render: (r) => (r.bucket === 'overdue' ? <Pill tone="bad">overdue</Pill> : r.status) },
                            { key: 'act', label: '', render: (r) => (r.status === 'Open'
                                ? <Button variant="subtle" onClick={() => taskDone(r, 'Completed')}>Done</Button>
                                : <Button variant="ghost" onClick={() => taskDone(r, 'Open')}>Reopen</Button>) },
                        ]} rows={[...open, ...c.tasks.filter((t) => t.status !== 'Open').slice(0, 10)]} rowTone={(r) => (r.bucket === 'overdue' ? 'bad' : null)}
                            empty="No follow-ups." />
                    </Panel>
                    <Panel title="History" subtitle={c.client_id ? 'Includes what was logged on the client record.' : undefined}>
                        {c.activities.length === 0 ? <p className="text-sm text-slate-500">Nothing logged yet.</p> : (
                            <ul className="divide-y divide-slate-100">
                                {c.activities.map((a) => (
                                    <li key={a.activity_id} className="py-2 text-sm">
                                        <div className="flex flex-wrap gap-2 items-center">
                                            <Pill tone={a.activity_type === 'Note' ? 'slate' : 'indigo'}>{a.activity_type}</Pill>
                                            {a.direction !== 'N/A' && <span className="text-xs text-slate-500">{a.direction}</span>}
                                            <span className="text-xs text-slate-500">{new Date(a.occurred_at).toLocaleString()}</span>
                                            {a.agent_name && <span className="text-xs text-slate-400">{a.agent_name}</span>}
                                            {a.compliance_decision === 'blocked' && <Pill tone="bad">logged against a block</Pill>}
                                            {a.compliance_decision === 'not_checked' && <Pill tone="warn">not checked first</Pill>}
                                        </div>
                                        {a.subject && <div className="font-medium text-slate-800 mt-0.5">{a.subject}</div>}
                                        {a.body && <div className="text-slate-600 whitespace-pre-wrap">{a.body}</div>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Panel>
                </div>
            </div>

            {modal?.kind === 'edit' && (
                <Modal title="Edit card" onClose={() => setModal(null)} wide>
                    <ContactForm meta={meta} agents={meta.can_reassign ? agents : []} linked={!!c.client_id}
                        initial={{ ...c, assigned_agent_id: c.assigned_agent_id ?? '', touch_every_days: c.touch_every_days ?? '' }}
                        onCancel={() => setModal(null)}
                        onSave={(b) => {
                            const body = { ...b };
                            if (!meta.can_reassign) delete body.assigned_agent_id;
                            saveCard(body);
                        }} />
                </Modal>
            )}
            {modal?.kind === 'log' && (
                <LogModal meta={meta} contact={c} preset={modal.preset} onClose={() => setModal(null)}
                    onSaved={(r) => { setModal(null); setNotice({ tone: r.warning ? 'warn' : 'good', message: r.warning || `Logged.${r.follow_up ? ` Follow-up set for ${fmtDate(r.follow_up.due_date)}.` : ''}` }); load(); }} />
            )}
            {modal?.kind === 'opp' && <OppModal meta={meta} contact={c} opp={modal.opp} agents={agents} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
            {modal?.kind === 'stage' && <StageModal meta={meta} opp={modal.opp} to={modal.to} onClose={() => setModal(null)}
                onSaved={(msg) => { setModal(null); setNotice({ tone: 'good', message: msg || 'Moved.' }); load(); }} />}
            {modal?.kind === 'txn' && <TransactionModal contact={c} opp={modal.opp} onClose={() => setModal(null)}
                onSaved={(msg) => { setModal(null); setNotice({ tone: 'good', message: msg }); load(); }} />}
        </div>
    );
};

export default CrmContactPage;
